import { Breadcrumb, Button, Card, Col, Container, Dropdown, DropdownButton, Form, Image as Img, InputGroup, Modal, Row, Tab, Table, Tabs } from "react-bootstrap";
import Header from "../../components/common/Header";
import { Link, useNavigate, useParams } from "react-router-dom";
import { providerService, recipes } from "../../lib/services";
import React, { useEffect, useState } from "react";
import { icons } from "../../components/common/Icons";
import { QueryResults } from "../../lib/services/ReadOnlyService";
import { Recipe } from "../../lib/services/RecipeService";
import { useAlerts } from "../../components/notifications/AlertContext";


const THUMBNAIL_MAX = 200;
const LOSSY_COMPRESSION = 0.8;

interface ProviderState {
    readonly selectedName: string;
    readonly lookupId: string;
    readonly search: string;
    readonly loading: boolean;
    readonly items: Recipe[];
    readonly selectedRecipe?: Recipe;
    readonly showModal: boolean;
}

interface ImportRecipeElementState {
    readonly loading: boolean;
}

interface ImportRecipeButtonProps {
    readonly selectedRecipe?: Recipe;
    readonly disabled: boolean;
}

function ImportRecipeButton(props: ImportRecipeButtonProps) {
    const defaultState: ImportRecipeElementState = {
        loading: false,
    };
    const [ state, setState ] = useState(defaultState);
    const canvas = React.createRef<HTMLCanvasElement>();
    const alerts = useAlerts();
    const navigate = useNavigate();

    useEffect(() => {
        let isMounted = true;
        if (canvas.current && isMounted && state.loading) {
            let context = canvas.current.getContext("2d");
            let image = new Image();
            image.crossOrigin = "anonymous";
            image.src = props.selectedRecipe?.thumbnail || '';
            image.onload = () => {
                let ratio = Math.min(image.width, image.height);
                context?.clearRect(0, 0, THUMBNAIL_MAX, THUMBNAIL_MAX);
                context?.drawImage(image, 0, 0, ratio, ratio, 0, 0, canvas.current?.width || THUMBNAIL_MAX, canvas.current?.height || THUMBNAIL_MAX);
                let thumbnail = canvas.current?.toDataURL('image/jpeg', LOSSY_COMPRESSION);

                recipes.create({
                    ...props.selectedRecipe,
                    thumbnail,
                })
                .then(resp => {
                    alerts.success(`Successfully import ${resp.name}`);
                    navigate(`/recipes/${resp.recipeId}`);
                })
                .catch(e => {
                    alerts.error(`Failed to import recipe ${props.selectedRecipe?.name}: ${e.message}`);
                    setState({
                        ...state,
                        loading: false,
                    })
                });
            };
        }
    });

    return (
        <>
            <canvas hidden={true} ref={canvas} width={`${THUMBNAIL_MAX}px`} height={`${THUMBNAIL_MAX}px`}/>
            <Button disabled={props.disabled || state.loading} onClick={() => setState({...state, loading: true})} variant="success">Import</Button>
        </>
    );
}

function ProviderForm() {
    const { providerName } = useParams();
    const provider = providerService.provider(providerName ?? '');
    const defaultProviderState: ProviderState = {
        selectedName: 'Random',
        lookupId: '',
        search: '',
        loading: false,
        showModal: false,
        items: [],
    };
    const [ state, setState ] = useState(defaultProviderState);
    const alerts = useAlerts();
    useEffect(() => {
        let isMounted = true;
        if (state.loading) {
            let thunk: Promise<QueryResults<Recipe>>;
            switch (state.selectedName) {
                case 'Search':
                    thunk = provider.list({ search: state.search });
                    break;
                case 'Lookup':
                    thunk = provider.lookup(state.lookupId).list();
                    break;
                default:
                    thunk = provider.random().list();
            }
            thunk
                .then(resp => {
                    if (isMounted) {
                        setState({
                            ...state,
                            loading: false,
                            items: resp.items,
                        })
                    }
                })
                .catch(e => {
                    alerts.error(`Failed to ${state.selectedName}: ${e.message}`);
                    setState({
                        ...state,
                        loading: false,
                    });
                });
        }
        return () => {
            isMounted = false;
        }
    });

    return (
        <>
            <Modal fullscreen="lg-down" size="xl" show={state.showModal} onHide={() => setState({...state, showModal: false})}>
                <Modal.Header closeButton>
                    <Modal.Title>{state.selectedRecipe?.name}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Row className="text-center">
                        <Col>
                            <Img thumbnail src={state.selectedRecipe?.thumbnail} alt={state.selectedRecipe?.name}/>
                        </Col>
                    </Row>
                    <hr/>
                    <Row>
                        <Col>
                            <Tabs defaultActiveKey="ingredients" transition={false}>
                                <Tab title="Instructions" eventKey="instructions">
                                    <pre className="mt-2">
                                        {state.selectedRecipe?.instructions}
                                    </pre>
                                </Tab>
                                <Tab title="Ingredients" eventKey="ingredients">
                                    <Table responsive>
                                        <thead>
                                            <tr>
                                                <th>Ingredient</th>
                                                <th>Measurement</th>
                                                <th>Amount</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {state.selectedRecipe?.ingredients.map((item, index) => {
                                                return (
                                                    <tr key={`ingredient-${index}`}>
                                                        <td>{item.name}</td>
                                                        <td>{item.measurement}</td>
                                                        <td>{item.amount}</td>
                                                    </tr>
                                                )
                                            })}
                                        </tbody>
                                    </Table>
                                </Tab>
                            </Tabs>
                        </Col>
                    </Row>
                </Modal.Body>
                <Modal.Footer>
                    <ImportRecipeButton selectedRecipe={state.selectedRecipe} disabled={state.loading}/>
                    <Button variant="outline-secondary" target="_blank" rel="noreferrer" href={`https://www.themealdb.com/meal/${state.selectedRecipe?.recipeId}-${state.selectedRecipe?.name.replace(' ', '-').toLowerCase()}-recipe`}>Website <>{icons.icon('box-arrow-right')}</></Button>
                    <Button variant="outline-secondary" onClick={() => setState({...state, showModal: false})} >Close</Button>
                </Modal.Footer>
            </Modal>
            <Breadcrumb className="pt-3 ps-3 pb-1" style={{ backgroundColor: 'rgba(0, 0, 0, 0.03)' }}>
                <Breadcrumb.Item linkAs={Link} linkProps={{ to: "/providers" }}>Providers</Breadcrumb.Item>
                <Breadcrumb.Item active>{providerName}</Breadcrumb.Item>
            </Breadcrumb>
            <Container>
                <Header>Provider {providerName}</Header>
                <Row>
                    <Col>
                        <InputGroup>
                            <DropdownButton
                                disabled={state.loading}
                                id="operation"
                                variant="outline-secondary"
                                title={state.selectedName}
                            >
                                <Dropdown.Item onClick={() => setState({...state, selectedName: 'Random'})}>Random</Dropdown.Item>
                                <Dropdown.Item onClick={() => setState({...state, selectedName: 'Lookup'})}>Lookup</Dropdown.Item>
                                <Dropdown.Item onClick={() => setState({...state, selectedName: 'Search'})}>Search</Dropdown.Item>
                            </DropdownButton>
                            {state.selectedName === 'Lookup' &&
                                <Form.Control
                                    disabled={state.loading}
                                    value={state.lookupId}
                                    onChange={e => setState({...state, lookupId: e.target.value})}
                                />
                            }
                            {state.selectedName === 'Search' &&
                                <Form.Control
                                    disabled={state.loading}
                                    value={state.search}
                                    onChange={e => setState({...state, search: e.target.value})}
                                />
                            }
                            <Button
                                disabled={state.loading}
                                variant="success"
                                onClick={() => setState({...state, loading: true})}
                            >
                                <>{icons.icon('search')}</>
                            </Button>
                        </InputGroup>
                    </Col>
                </Row>
                {state.items.length > 0 &&
                    <>
                        <hr/>
                        <Row xs={1} lg={2} md={2}>
                        {state.items.map(item => {
                            return (
                                <Col className="mb-4" key={item.recipeId}>
                                    <Card>
                                        <Card.Header>{item.name}</Card.Header>
                                        <Card.Img className="clickable-img" onClick={() => setState({...state, selectedRecipe: item, showModal: true})} src={item.thumbnail}/>
                                        <Card.Footer className="text-center">
                                            <ImportRecipeButton disabled={false} selectedRecipe={item}/>
                                            <Button className="ms-2 me-2" variant="outline-secondary" target="_blank" rel="noreferrer" href={`https://www.themealdb.com/meal/${state.selectedRecipe?.recipeId}-${state.selectedRecipe?.name.replace(' ', '-').toLowerCase()}-recipe`}>Website <>{icons.icon('box-arrow-right')}</></Button>
                                            <Button
                                                variant="outline-secondary"
                                                onClick={() => setState({...state, selectedRecipe: item, showModal: true})}
                                            >
                                                Details
                                            </Button>
                                        </Card.Footer>
                                    </Card>
                                </Col>
                            );
                        })}
                        </Row>
                    </>
                }
            </Container>
        </>
    );
}

export default ProviderForm;